/**
 * Entry point for the heraldic curtain transition.
 *
 * Call this instead of `scene.start(...)` wherever the game changes to a
 * genuinely different screen. Overlay scenes launched on top of a live one
 * (SettingsScene, MapSelectScene, the HUD) should keep using `scene.launch`
 * directly — curtaining a modal would read as a bug, not a flourish.
 *
 *   goTo(this, "MenuScene");
 *   goTo(this, "GameScene", { stop: ["MenuScene"], launch: ["UIScene"] });
 *
 * Re-entrancy: the curtain is a scene, so a second call while one is already
 * running would stack two curtains and race the swap. `goTo` drops any call
 * made while a transition is in flight.
 */
import Phaser from "phaser";
import { TransitionData } from "../scenes/TransitionScene";

export interface GoToOptions {
  /** Extra scenes to stop while the screen is covered. */
  stop?: string[];
  /** Scenes to launch alongside the target (e.g. "UIScene"). */
  launch?: string[];
  /** Set false to omit the crest (used for the first curtain out of Boot). */
  crest?: boolean;
}

/** True while a curtain is mid-flight; blocks overlapping navigations. */
let inFlight = false;

export function isTransitioning(): boolean {
  return inFlight;
}

export function goTo(scene: Phaser.Scene, target: string, opts: GoToOptions = {}): void {
  if (inFlight) return;

  const manager = scene.scene;
  // The curtain needs its textures; if they never generated (or the scene is
  // missing) fall back to a hard cut rather than stranding the player.
  if (!manager.get("TransitionScene") || !scene.textures.exists("ui-banner-half")) {
    for (const key of opts.stop ?? []) manager.stop(key);
    manager.start(target);
    for (const key of opts.launch ?? []) manager.launch(key);
    return;
  }

  inFlight = true;

  // The calling scene stops itself as part of the swap unless it is the target.
  const stop = new Set(opts.stop ?? []);
  if (scene.scene.key !== target) stop.add(scene.scene.key);

  const data: TransitionData = {
    target,
    stop: [...stop],
    launch: opts.launch,
    crest: opts.crest
  };

  const transition = manager.get("TransitionScene") as Phaser.Scene;
  // Clear the flag when the curtain finishes, however it ends.
  transition.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { inFlight = false; });
  // Failsafe: if that shutdown never arrives (a throw mid-sequence, a scene
  // torn down from elsewhere), a stuck flag would soft-lock *every* navigation
  // in the game. Release it unconditionally well after the curtain should be
  // done. setTimeout, not scene.time, so it survives the caller being stopped.
  setTimeout(() => { inFlight = false; }, 2500);
  manager.launch("TransitionScene", data);
}
